import { useState } from 'react'
import ContentSection from 'components/content-section'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Box,
  IconButton,
  Tooltip,
} from '@material-ui/core'
import PersonOutlineIcon from 'components/material-symbol/icons/PersonOutline'
import EmailOutlineIcon from 'components/material-symbol/icons/Email'
import PhoneOutlineIcon from 'components/material-symbol/icons/Phone'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import DeleteIcon from 'components/material-symbol/icons/Delete'
import { styled } from '@material-ui/core/styles'
import { formatPhoneNumber } from 'utils/phoneNumber'
import { useMutation } from '@apollo/client'
import { useSnackbar, useSnackbarError, useSnackbarSuccess } from 'lib/snackbar'
import DeleteGuardianDialog from './delete-guardian-dialog'
import { DELETE_REPRESENTATIVE } from './queries'

const StyledHeadline = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  fontWeight: 400,
  color: theme.palette.text.secondary,
}))

const StyledContainer = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}))

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  [theme.breakpoints.down('xs')]: {
    '& span': {
      fontSize: '0.875rem',
      overflowWrap: 'anywhere',
    },
  },
}))

const InfoItem = ({ icon, primaryText }) => {
  return (
    <ListItem>
      <ListItemAvatar style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
        {icon}
      </ListItemAvatar>
      <StyledListItemText primary={primaryText} />
    </ListItem>
  )
}

const GuardianItem = ({ icon, guardian, onDelete }) => {
  return (
    <ListItem>
      <ListItemAvatar style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
        {icon}
      </ListItemAvatar>
      <StyledListItemText
        primary={`${guardian.contact.firstName} ${guardian.contact.lastName} (${guardian.contact.email})`}
      />
      <Tooltip title="Delete Guardian">
        <IconButton
          onClick={() => onDelete(guardian.contact.id)}
          aria-label="delete guardian"
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </ListItem>
  )
}

const ParticipantInformation = ({ participant, refetch }) => {
  const {
    firstName,
    lastName,
    isMinor,
    contact,
    medicalProfile,
    representatives,
  } = participant
  const representative = isMinor ? 'Guardian' : 'Contact'
  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [guardianToDelete, setGuardianToDelete] = useState(null)

  const [deleteStudentGuardian, { loading }] = useMutation(
    DELETE_REPRESENTATIVE,
  )

  const handleDeleteClick = representative => {
    setGuardianToDelete(representative)
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (guardianToDelete) {
      try {
        const { data } = await deleteStudentGuardian({
          variables: {
            representativeId: guardianToDelete.id,
          },
        })
        if (data?.deleteRepresentative.success) {
          snackbarSuccess('Guardian was successfully deleted')
          setDeleteModalOpen(false)
          setGuardianToDelete(null)
          refetch()
        } else {
          snackbarError(
            `Error deleting guardian: ${data?.deleteRepresentative.message}`,
          )
        }
      } catch (error) {
        snackbarError(`Error deleting guardian: ${error.message}`)
      }
    }
  }

  const otherGuardians = representatives.filter(
    r => r.relationshipType !== 'SELF' && r.contact?.id !== contact?.id,
  )

  return (
    <StyledContainer>
      <ContentSection title="Participant Information">
        <Grid container>
          <Grid xs={12} sm={6} item>
            <StyledHeadline>Student Name</StyledHeadline>
            <InfoItem
              icon={<PersonOutlineIcon />}
              primaryText={`${firstName} ${lastName}`}
            />
          </Grid>
          <Grid xs={12} sm={6} item>
            <StyledHeadline>{representative} Name</StyledHeadline>
            <InfoItem
              icon={<PersonOutlineIcon />}
              primaryText={`${contact?.firstName} ${contact?.lastName}`}
            />
          </Grid>
          <Grid xs={12} item>
            <StyledHeadline>{representative} Information</StyledHeadline>
          </Grid>
          <Grid xs={12} sm={6} item>
            <InfoItem
              icon={<EmailOutlineIcon />}
              primaryText={contact?.email || 'N/A'}
            />
          </Grid>
          <Grid xs={12} sm={6} item>
            <InfoItem
              icon={<PhoneOutlineIcon />}
              primaryText={formatPhoneNumber(contact?.phone) || 'N/A'}
            />
          </Grid>
          {isMinor && otherGuardians.length > 0 && (
            <>
              <Grid xs={12} item>
                <StyledHeadline>Other Guardians</StyledHeadline>
              </Grid>
              {otherGuardians.map(r => (
                <Grid xs={12} sm={6} item key={r.contact.id}>
                  <GuardianItem
                    icon={<PersonOutlineIcon />}
                    guardian={r}
                    onDelete={() => handleDeleteClick(r)}
                  />
                </Grid>
              ))}
            </>
          )}
          {medicalProfile?.info && (
            <>
              <Grid xs={12} item>
                <StyledHeadline>Allergies and Medications</StyledHeadline>
              </Grid>
              <Grid xs={12} item>
                <InfoItem
                  icon={<InfoOutlinedIcon />}
                  primaryText={medicalProfile.info}
                />
              </Grid>
            </>
          )}
        </Grid>
      </ContentSection>

      <DeleteGuardianDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        guardian={guardianToDelete}
        onDelete={handleConfirmDelete}
        loading={loading}
      />
    </StyledContainer>
  )
}

export default ParticipantInformation
